'use client'

import * as React from 'react'
import { flushSync } from 'react-dom'
import { useTheme } from '@/components/theme-provider'

export function useAnimatedTheme() {
  const { theme, setTheme } = useTheme()
  const [isAnimating, setIsAnimating] = React.useState(false)

  const setAnimatedTheme = React.useCallback(
    async (
      newTheme: 'light' | 'dark' | 'system',
      clickEvent?: React.MouseEvent
    ) => {
      if (newTheme === theme || isAnimating) return

      setIsAnimating(true)

      // Check if browser supports View Transitions API
      if (!document.startViewTransition) {
        // Fallback for browsers without View Transitions API
        setTheme(newTheme)
        setIsAnimating(false)
        return
      }

      try {
        // Start the view transition
        await document.startViewTransition(() => {
          flushSync(() => {
            setTheme(newTheme)
          })
        }).ready

        // Calculate animation origin
        let x, y, maxRad

        if (clickEvent) {
          // Use click position if available
          x = clickEvent.clientX
          y = clickEvent.clientY
          const right = window.innerWidth - x
          const bottom = window.innerHeight - y
          maxRad = Math.hypot(Math.max(x, right), Math.max(y, bottom))
        } else {
          // Default to bottom-left
          x = 0
          y = window.innerHeight
          maxRad = Math.hypot(window.innerWidth, window.innerHeight)
        }

        // Animate the new root with clip-path
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${maxRad}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 700,
            easing: 'ease-in-out',
            pseudoElement: '::view-transition-new(root)',
          }
        )
      } catch (error) {
        // Fallback if transition fails
        console.warn('View transition failed:', error)
      } finally {
        setTimeout(() => setIsAnimating(false), 700)
      }
    },
    [theme, setTheme, isAnimating]
  )

  return {
    theme,
    setTheme: setAnimatedTheme,
    isAnimating,
  }
}
